/* ActieCentrum — 5 belangrijkste acties van vandaag voor directie/management.
 * Rangschikt op urgentie (rood → oranje → paars → groen).
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { AlertTriangle, ChevronRight, Zap, Loader2 } from "lucide-react";

const TONE = {
  rood: {
    bg: "from-red-50 to-orange-50",
    border: "border-red-300",
    icon: "text-red-700",
    badge: "bg-red-100 text-red-800 border-red-200",
    label: "Actie vereist",
  },
  oranje: {
    bg: "from-amber-50 to-yellow-50",
    border: "border-amber-300",
    icon: "text-amber-700",
    badge: "bg-amber-100 text-amber-800 border-amber-200",
    label: "Let op",
  },
  paars: {
    bg: "from-purple-50 to-fuchsia-50",
    border: "border-purple-300",
    icon: "text-purple-700",
    badge: "bg-purple-100 text-purple-800 border-purple-200",
    label: "Kans",
  },
  groen: {
    bg: "from-emerald-50 to-teal-50",
    border: "border-emerald-300",
    icon: "text-emerald-700",
    badge: "bg-emerald-100 text-emerald-800 border-emerald-200",
    label: "In orde",
  },
};

export default function ActieCentrum() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const nav = useNavigate();

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await api.get("/directie/actiecentrum");
      setData(r.data);
    } catch (e) {
      // 403 → gebruiker heeft geen management-rol; verberg component stil.
      if (e?.response?.status === 403) {
        setData({ cards: [], totaal_signalen: 0 });
      } else {
        setError(e?.response?.data?.detail || "Actiecentrum niet beschikbaar");
      }
    }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  if (loading) {
    return (
      <Card className="p-4 mb-4" data-testid="actiecentrum-loading">
        <div className="flex items-center gap-2 text-slate-500 text-sm">
          <Loader2 className="h-4 w-4 animate-spin" /> Actiecentrum wordt geladen …
        </div>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className="p-4 mb-4 border-2 border-red-200 bg-red-50" data-testid="actiecentrum-error">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 text-red-800 text-sm">
            <AlertTriangle className="h-4 w-4" />
            <span>Actiecentrum kon niet worden geladen: {String(error)}</span>
          </div>
          <Button size="sm" variant="outline" onClick={load} data-testid="actiecentrum-retry">
            Opnieuw proberen
          </Button>
        </div>
      </Card>
    );
  }

  const cards = data?.cards || [];
  if (cards.length === 0) return null;

  return (
    <Card className="p-4 mb-4 border-2 border-slate-300"
          data-testid="actiecentrum-card">
      <div className="flex items-center gap-2 mb-3">
        <Zap className="h-5 w-5 text-amber-600" />
        <h3 className="font-semibold text-base">Actiecentrum · vandaag</h3>
        <span className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 border border-slate-200 ml-auto">
          {cards.length} van {data.totaal_signalen} signalen
        </span>
      </div>
      <ul className="space-y-2">
        {cards.map((c, i) => {
          const t = TONE[c.urgentie] || TONE.paars;
          return (
            <li key={c.id}
                data-testid={`actie-${c.id}`}
                className={`bg-gradient-to-br ${t.bg} border-2 ${t.border} rounded-lg p-3`}>
              <div className="flex items-start gap-3">
                <AlertTriangle className={`h-5 w-5 flex-shrink-0 mt-0.5 ${t.icon}`} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                    <span className="font-semibold text-slate-900">{c.titel}</span>
                    <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full border font-semibold ${t.badge}`}>
                      {t.label}
                    </span>
                  </div>
                  <p className="text-sm text-slate-700 leading-snug">{c.waarom}</p>
                  {c.effect && (
                    <p className="text-xs text-slate-600 mt-1 italic">→ {c.effect}</p>
                  )}
                  {c.details && c.details.length > 0 && (
                    <ul className="mt-2 space-y-0.5">
                      {c.details.map((d, di) => (
                        <li key={di} className="text-xs text-slate-600 font-mono truncate">· {d}</li>
                      ))}
                    </ul>
                  )}
                  <div className="mt-2 flex items-center gap-2">
                    <Button size="sm" variant="ghost"
                            data-testid={`actie-${c.id}-btn`}
                            onClick={() => nav(c.actie_link)}
                            className="h-7 px-2 gap-1 hover:bg-white/60">
                      {c.actie}
                      <ChevronRight className="h-3 w-3" />
                    </Button>
                    <span className="text-[10px] text-slate-400" title={c.bron}>
                      Bron: {c.bron?.slice(0, 60)}
                    </span>
                  </div>
                </div>
              </div>
            </li>
          );
        })}
      </ul>
    </Card>
  );
}
